import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { meApi } from "../api/me.api";
import { useAuth } from "../hooks/useAuth";
import { AvatarPicker } from "../components/AvatarPicker";
import { type ThemeChoice } from "../lib/theme";
import { useThemeChoice } from "../hooks/useThemeChoice";
import { SignaturePad } from "../components/SignaturePad";

/* ---------------------------------------------------------------------------
 * PARKED with the sections below (Notifications, Mobile app updates, Outgoing
 * mail). The JSX for all three is commented out further down; these are the
 * imports, constants, state and handlers that fed it.
 *
 * Commented rather than deleted so the feature comes back as one piece -
 * uncomment this block and the JSX together. Left live, they fail the build
 * with TS6133 "declared but never read", which is what broke CI.
 *
 * import {
 *   NOTIFICATION_KIND_LABEL,
 *   type NotificationKind,
 *   type NotificationPreferenceItem,
 * } from "@todo/shared";
 * import { enableWebPush, type WebPushStatus } from "../lib/push";
 * import { AppUpdateSettings } from "../components/AppUpdateSettings";
 * import { MailPolicySettings } from "../components/MailPolicySettings";
 * ------------------------------------------------------------------------ */

/* PARKED - see the note at the top of this file.
 *
 * /** What the "Enable push" attempt came back with, in words. *\/
 * const PUSH_STATUS_MESSAGE: Record<WebPushStatus, string> = {
 *   registered:
 *     "Push is active in this browser - banners arrive even when the portal is closed.",
 *   unconfigured: "Push is not configured on the server (Firebase settings missing).",
 *   unsupported:
 *     "This browser does not support web push. Safari needs version 16.4 or newer.",
 *   denied:
 *     "Notifications are blocked for this site - allow them in the browser's settings, then try again.",
 *   failed: "Could not enable push - the browser console has the reason.",
 * };
 */

/**
 * Module 14 - Settings.
 *
 * Appearance first; more sections (notification preferences, defaults) can
 * stack below as they land. The theme is a DEVICE preference - localStorage,
 * not the database - so your phone can be dark while your desktop is light.
 */

const THEMES: { value: ThemeChoice; label: string; hint: string }[] = [
  { value: "light", label: "Light", hint: "Always light, everywhere." },
  { value: "dark", label: "Dark", hint: "Always dark, easy on the eyes." },
  { value: "system", label: "System", hint: "Follows your operating system." },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/* PARKED - see the note at the top of this file.
 *
 * const DELIVERY_LABEL: Record<NotificationPreferenceItem["delivery"], string> = {
 *   BOTH: "In-app + push",
 *   IN_APP: "In-app only",
 *   PUSH: "Push only",
 * };
 */

export default function SettingsPage() {
  const { user, refreshProfile } = useAuth();
  // Shared with the dock's Appearance button, so changing it there is
  // already reflected here (and the other way round).
  const { choice, setChoice } = useThemeChoice();
  const [flash, setFlash] = useState<string | null>(null);

  /* PARKED - see the note at the top of this file.
   *
   * // ---- push on THIS browser. Safari never shows the permission prompt
   * // for the automatic post-login attempt - only a real click may ask -
   * // so this button is the way Safari users opt in.
   * const [devicePush, setDevicePush] = useState<"idle" | "busy" | WebPushStatus>("idle");
   * async function enablePushHere() {
   *   setDevicePush("busy");
   *   setDevicePush(await enableWebPush());
   * }
   */

  // ---- profile
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [jobTitle, setJobTitle] = useState(user?.jobTitle ?? "");
  const [photo, setPhoto] = useState<File | null>(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // ---- password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  /* PARKED - see the note at the top of this file.
   *
   * // ---- notifications
   * const [prefs, setPrefs] = useState<NotificationPreferenceItem[] | null>(null);
   */

  // E-Signature: the saved image's preview URL, plus edit state.
  const [esignUrl, setEsignUrl] = useState<string | null>(null);
  const [esignEditing, setEsignEditing] = useState(false);
  const [esignDraft, setEsignDraft] = useState<string | null>(null);
  const [esignBusy, setEsignBusy] = useState(false);

  useEffect(() => {
    void meApi.getESign().then((r) => setEsignUrl(r.url)).catch(() => undefined);
  }, []);

  async function saveESign() {
    if (!esignDraft) return;
    setEsignBusy(true);
    try {
      const r = await meApi.saveESign(esignDraft);
      setEsignUrl(r.url);
      setEsignEditing(false);
      setEsignDraft(null);
      setFlash("Signature saved.");
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : "Could not save the signature.");
    } finally {
      setEsignBusy(false);
    }
  }

  async function removeESign() {
    setEsignBusy(true);
    try {
      await meApi.removeESign();
      setEsignUrl(null);
      setEsignDraft(null);
      setFlash("Signature removed.");
    } catch {
      setFlash("Could not remove the signature.");
    } finally {
      setEsignBusy(false);
    }
  }
  /* PARKED - see the note at the top of this file.
   *
   * const [prefsBusy, setPrefsBusy] = useState(false);
   * const [prefsDirty, setPrefsDirty] = useState(false);
   *
   * useEffect(() => {
   *   let cancelled = false;
   *   void meApi
   *     .notificationPreferences()
   *     .then((items) => { if (!cancelled) setPrefs(items); })
   *     .catch(() => { if (!cancelled) setPrefs([]); });
   *   return () => { cancelled = true; };
   * }, []);
   */

  function pick(value: ThemeChoice) {
    setChoice(value);
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setProfileBusy(true);
    setProfileError(null);
    try {
      await meApi.updateProfile({
        displayName: displayName.trim(),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
      });
      if (photo) {
        await meApi.setAvatar(photo);
        setPhoto(null);
      }
      await refreshProfile();
      setFlash("Profile saved.");
    } catch (err) {
      setProfileError(
        err instanceof ApiError ? err.message : "Could not save your profile",
      );
    } finally {
      setProfileBusy(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError("The new passwords do not match");
      return;
    }
    setPasswordBusy(true);
    setPasswordError(null);
    try {
      await meApi.changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFlash("Password changed. Use the new one next time you sign in.");
    } catch (err) {
      setPasswordError(
        err instanceof ApiError ? err.message : "Could not change the password",
      );
    } finally {
      setPasswordBusy(false);
    }
  }

  /* PARKED - see the note at the top of this file.
   *
   * function updatePref(
   *   kind: NotificationKind,
   *   changes: Partial<NotificationPreferenceItem>,
   * ) {
   *   setPrefs((current) =>
   *     current
   *       ? current.map((p) =>
   *           p.notificationType === kind ? { ...p, ...changes } : p,
   *         )
   *       : current,
   *   );
   *   setPrefsDirty(true);
   * }
   *
   * async function savePrefs() {
   *   if (!prefs) return;
   *   setPrefsBusy(true);
   *   try {
   *     await meApi.saveNotificationPreferences(prefs);
   *     setPrefsDirty(false);
   *     setFlash("Notification preferences saved.");
   *   } catch (err) {
   *     setFlash(
   *       err instanceof ApiError ? err.message : "Could not save the preferences",
   *     );
   *   } finally {
   *     setPrefsBusy(false);
   *   }
   * }
   */

  return (
    <div className="mx-auto  px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Signed in as {user?.displayName} · {user?.role.name}
        </p>
      </header>

      {flash && (
        <div
          role="status"
          className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200"
        >
          <span>{flash}</span>
          <button
            type="button"
            onClick={() => setFlash(null)}
            aria-label="Dismiss"
            className="text-emerald-600 hover:text-emerald-800"
          >
            &times;
          </button>
        </div>
      )}


      <div className="mt-6 grid items-start gap-6 lg:grid-cols-2">
        {/* ---- My profile */}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">My profile</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Your email and role are managed by an administrator.
          </p>

          <form
            onSubmit={(e) => void saveProfile(e)}
            className="mt-4 space-y-4"
          >
            {profileError && (
              <p
                role="alert"
                className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100"
              >
                {profileError}
              </p>
            )}

            <AvatarPicker
              file={photo}
              onChange={setPhoto}
              fallback={initials(displayName || (user?.displayName ?? ""))}
              currentUrl={user?.avatarUrl}
              disabled={profileBusy}
            />

            <div>
              <label
                htmlFor="me-name"
                className="block text-sm font-medium text-slate-700"
              >
                Full name
              </label>
              <input
                id="me-name"
                value={displayName}
                maxLength={120}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <span className="block text-sm font-medium text-slate-700">
                Email
              </span>
              <p className="mt-1.5 w-full truncate rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500 ring-1 ring-slate-200">
                {user?.email ?? '\u2014'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Your sign-in identity. Only an administrator can change it, from
                the People page.
              </p>
            </div>
            <div>
              <label
                htmlFor="me-title"
                className="block text-sm font-medium text-slate-700"
              >
                Job title{" "}
                <span className="ml-1 text-slate-400">(optional)</span>
              </label>
              <input
                id="me-title"
                value={jobTitle}
                maxLength={80}
                onChange={(e) => setJobTitle(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>

                    {/* ---- E-Signature: saved once here, offered as "use my saved
             signature" whenever CG Sign asks this person to sign. */}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">E-Signature</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Save your signature once - when a CG Sign document reaches your
            email, you can sign with one click.
          </p>

          {esignUrl && !esignEditing ? (
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <img
                src={esignUrl}
                alt="Your saved signature"
                className="h-16 rounded-lg border border-slate-200 bg-white px-3 py-1"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEsignEditing(true)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Replace
                </button>
                <button
                  type="button"
                  disabled={esignBusy}
                  onClick={() => void removeESign()}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4 max-w-xl">
              <SignaturePad onChange={setEsignDraft} />
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  disabled={esignBusy || !esignDraft}
                  onClick={() => void saveESign()}
                  className="rounded-lg bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50"
                >
                  {esignBusy ? "Saving..." : "Save signature"}
                </button>
                {esignUrl && (
                  <button
                    type="button"
                    onClick={() => { setEsignEditing(false); setEsignDraft(null); }}
                    className="rounded-lg px-3 py-2 text-xs font-medium text-slate-500 transition hover:bg-slate-100"
                  >
                    Keep current
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={profileBusy || !displayName.trim()}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-60"
              >
                {profileBusy ? "Saving..." : "Save profile"}
              </button>
            </div>
          </form>
          
        </section>

        {/* ---- Change password */}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">
            Change password
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Forgot it entirely? An administrator can set you a new one from
            People.
          </p>

          <form
            onSubmit={(e) => void changePassword(e)}
            className="mt-4 space-y-4"
          >
            {passwordError && (
              <p
                role="alert"
                className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-100"
              >
                {passwordError}
              </p>
            )}

            <div>
              <label
                htmlFor="pw-current"
                className="block text-sm font-medium text-slate-700"
              >
                Current password
              </label>
              <input
                id="pw-current"
                type="password"
                value={currentPassword}
                autoComplete="current-password"
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setPasswordError(null);
                }}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label
                htmlFor="pw-new"
                className="block text-sm font-medium text-slate-700"
              >
                New password
              </label>
              <input
                id="pw-new"
                type="password"
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordError(null);
                }}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
              <p className="mt-1 text-xs text-slate-500">8 to 72 characters.</p>
            </div>
            <div>
              <label
                htmlFor="pw-confirm"
                className="block text-sm font-medium text-slate-700"
              >
                Confirm new password
              </label>
              <input
                id="pw-confirm"
                type="password"
                value={confirmPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError(null);
                }}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={
                  passwordBusy || !currentPassword || newPassword.length < 8
                }
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-60"
              >
                {passwordBusy ? "Saving..." : "Change password"}
              </button>
            </div>
          </form>
        </section>
      </div>
            <div className="mt-6 grid items-start gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Appearance</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Saved on this device - each device keeps its own choice. The
            round button in the bottom-right corner cycles the same setting
            from any page.
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {THEMES.map((theme) => (
              <button
                key={theme.value}
                type="button"
                onClick={() => pick(theme.value)}
                aria-pressed={choice === theme.value}
                className={[
                  "rounded-xl border p-4 text-left transition",
                  choice === theme.value
                    ? "border-brand-500 bg-brand-50 ring-2 ring-brand-100"
                    : "border-slate-200 bg-white hover:border-slate-300",
                ].join(" ")}
              >
                {/* A miniature of the theme itself - the swatch IS the preview. */}
                <span
                  aria-hidden="true"
                  className="flex h-10 overflow-hidden rounded-lg border border-slate-200"
                  style={{
                    background:
                      theme.value === "dark"
                        ? "#171c22"
                        : theme.value === "light"
                          ? "#f8fafc"
                          : "linear-gradient(110deg, #f8fafc 50%, #171c22 50%)",
                  }}
                >
                  <span
                    className="m-2 h-2 w-10 self-start rounded-full"
                    style={{
                      background:
                        theme.value === "dark" ? "#303a45" : "#cbd5e1",
                    }}
                  />
                </span>
                <span className="mt-2.5 block text-sm font-semibold text-slate-900">
                  {theme.label}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  {theme.hint}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                Notifications
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Choose what reaches you, and where. Push applies once devices
                are registered.
              </p>
            </div>
            {prefsDirty && (
              <button
                type="button"
                disabled={prefsBusy}
                onClick={() => void savePrefs()}
                className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
              >
                {prefsBusy ? "Saving..." : "Save changes"}
              </button>
            )}
          </div>

          {prefs === null ? (
            <div className="mt-4 space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-9 animate-pulse rounded-lg bg-slate-100"
                />
              ))}
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {prefs.map((pref) => (
                <li
                  key={pref.notificationType}
                  className="flex flex-wrap items-center gap-3 py-2.5"
                >
                  <span className="min-w-0 flex-1 text-sm text-slate-800">
                    {NOTIFICATION_KIND_LABEL[pref.notificationType]}
                  </span>
                  <select
                    value={pref.delivery}
                    disabled={!pref.enabled}
                    onChange={(e) =>
                      updatePref(pref.notificationType, {
                        delivery: e.target
                          .value as NotificationPreferenceItem["delivery"],
                      })
                    }
                    aria-label={`Delivery for ${NOTIFICATION_KIND_LABEL[pref.notificationType]}`}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100 disabled:opacity-40"
                  >
                    {(
                      Object.keys(DELIVERY_LABEL) as Array<
                        keyof typeof DELIVERY_LABEL
                      >
                    ).map((d) => (
                      <option key={d} value={d}>
                        {DELIVERY_LABEL[d]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pref.enabled}
                    aria-label={`${NOTIFICATION_KIND_LABEL[pref.notificationType]} notifications`}
                    onClick={() =>
                      updatePref(pref.notificationType, {
                        enabled: !pref.enabled,
                      })
                    }
                    className={[
                      "relative h-5 w-9 shrink-0 rounded-full transition",
                      pref.enabled ? "bg-brand-600" : "bg-slate-200",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
                        pref.enabled ? "left-[18px]" : "left-0.5",
                      ].join(" ")}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <p className="min-w-0 flex-1 text-xs text-slate-500">
              {devicePush !== "idle" && devicePush !== "busy"
                ? PUSH_STATUS_MESSAGE[devicePush]
                : "Push banners need a one-time permission on each browser. If you were never asked (Safari only asks from a click), use this button."}
            </p>
            <button
              type="button"
              disabled={devicePush === "busy"}
              onClick={() => void enablePushHere()}
              className="rounded-lg border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              {devicePush === "busy" ? "Enabling..." : "Enable push on this device"}
            </button>
          </div>
        </section> */}
      </div>
    </div>
  );
}
